/**
 * Read-adapter from the real `expenseClaim.list` wire shape (plus a narrow
 * employee/pay-run lookup) to the UI's `Expense` type (#22792). Pure, no
 * React — mirrors `wagepro_store_employees.ts`'s `deriveEmployeesFromRecords`
 * pattern: `RealExpenseClaimRecord` is the permanent adapter shape, and
 * `deriveExpensesFromRecords` is the permanent seam `use_expenses.ts` calls.
 *
 * ── Status mapping ───────────────────────────────────────────────────────
 *
 * The backend's `expense_claims.status` has SIX values
 * (`draft|submitted|approved|rejected|paid|voided`); the UI's `ExpenseStatus`
 * has FOUR (`pending|approved|paid|rejected`) — this was already true before
 * this pass, the mock fixture simply never emitted `draft`/`voided`.
 * `submitted` maps to `pending` (the UI's name for "awaiting a decision");
 * `approved`/`paid`/`rejected` pass through unchanged. `draft` (not yet
 * submitted for approval) and `voided` (withdrawn) are EXCLUDED from the
 * queue entirely — neither is a state a reviewer needs to act on or audit
 * from this screen. {@link isQueueableClaimStatus} is the single place that
 * decides this, so the exclusion and the mapping can never drift apart.
 *
 * ── Employee-name resolution ─────────────────────────────────────────────
 *
 * `expense_claims.submitter_user_id` is a platform user id, not an
 * `employees.id` row — {@link resolveSubmitterEmployee} joins it against
 * `payroll.listEmployees`' `employee_user_id` (added to `RealEmployeeRecord`
 * in this same pass — the real wire row already carried it, the adapter
 * interface just hadn't read it yet). An unmatched submitter (a since-
 * removed employee, or a submission from a user with no linked employee
 * row) resolves `employee_name: null` rather than throwing or fabricating a
 * name — `use_expenses.ts`'s caller renders an i18n'd fallback label for
 * this case (never a hardcoded English string baked into this adapter).
 *
 * ── Reimbursement period resolution ──────────────────────────────────────
 *
 * A `reimbursement_method='payroll'` claim only carries a resolvable pay
 * period once its `payroll_run_id` is set — see `Reimbursement.period`'s own
 * doc comment in `types.ts` for why that is a real, expected,
 * indefinitely-`null` state today (`payroll.finalisePayRun` isn't
 * OAuth-exposed yet, #22798), not a loading gap. `payload_run_id` is resolved
 * against a narrow `{id, period_start}` pay-run lookup — deliberately NOT
 * the full `RealPayRunRecord` bundle `use_pay_runs.ts` builds (which costs
 * an extra `getPayRun`/`getPayRunJournalPreview` round trip PER pay run):
 * this adapter only ever needs `period_start`, which `payroll.listPayRuns`'s
 * summary rows already carry.
 *
 * ── Category → claim number ──────────────────────────────────────────────
 *
 * The real backend has no expense-category concept (a claim's LINES carry a
 * GL `account_id`, not a category) — the UI's Category column is repointed
 * at the claim's own `claim_number` instead of fabricating a category
 * client-side. See `types.ts`'s `Expense.claim_number` doc comment.
 *
 * ── `submitted_on` source ─────────────────────────────────────────────────
 *
 * Sourced from `submitted_at` (set exactly once, by `expenseClaim.submit`,
 * to the moment a claim leaves `draft`), truncated to its date portion —
 * NOT `claim_date` (the expense transaction date the claimant typed in,
 * a different concept the mock never distinguished from "submitted"). Every
 * QUEUEABLE status (see below) is reachable only after `submit`, so
 * `submitted_at` is always non-null in practice for a row this adapter ever
 * sees; it is still typed nullable (the DB column has no `NOT NULL`
 * constraint) and falls back to `claim_date` rather than crashing or
 * rendering a blank cell if that invariant is ever violated by a legacy row.
 *
 * ── Raw wire strings, not literal unions ─────────────────────────────────
 *
 * `status`/`reimbursement_method` are plain TEXT columns with no `CHECK`
 * constraint (`apps/api/src/db/schema/tenant/expense_claims.ts`), so Drizzle
 * infers them as `string`, not the closed unions this module's logic treats
 * them as — mirrors `wagepro_store_pay_runs.ts`'s `RealPayRunRecord.status`
 * stance exactly (see that file's own doc comment): every comparison below
 * is a safe raw-string check, and an unrecognised value takes the safe
 * "not queueable" / "not payroll" default rather than throwing.
 */
import type { Expense, ExpenseStatus, PayPeriod } from './types';
import { derivePayPeriod } from './wagepro_store_pay_runs';

/**
 * The `expense_claims` wire fields this adapter reads. `expenseClaim.list`
 * returns the full Drizzle row (more columns than this — tax/IRD validation
 * bookkeeping, contact/bill linkage, ...); this interface is intentionally a
 * subset, not a mirror, so it stays stable across backend-only column
 * additions (mirrors `RealEmployeeRecord`/`RealPayRunRecord`'s own stance).
 */
export interface RealExpenseClaimRecord {
  id: string;
  claim_number: string;
  submitter_user_id: string;
  /** The expense transaction date (`claim_date`, always present) — the fallback source for `submitted_on` when `submitted_at` is null. See module docblock. */
  claim_date: string;
  /** Set once, by `expenseClaim.submit` — see module docblock for why this (not `claim_date`) is `submitted_on`'s real source. */
  submitted_at: string | null;
  /** Raw wire string: `draft`/`submitted`/`approved`/`rejected`/`paid`/`voided` in practice — see module docblock. */
  status: string;
  currency_code: string;
  total_cents: number;
  /** Raw wire string: `bill`/`payroll` in practice — see module docblock. */
  reimbursement_method: string;
  payroll_run_id: string | null;
}

/** The narrow employee-lookup shape this adapter needs — a subset of `RealEmployeeRecord` (`wagepro_store_employees.ts`), structurally satisfied by it. */
export interface ExpenseSubmitterLookup {
  id: string;
  full_name: string;
  employee_user_id: string | null;
}

/** The narrow pay-run-lookup shape this adapter needs — a subset of `payroll.listPayRuns`' summary rows (`use_pay_runs.ts`'s `PayRunSummaryRow`), structurally satisfied by it. */
export interface ExpensePayRunLookup {
  id: string;
  period_start: string;
}

/** The one backend status that maps to the UI's `Reimbursement`-agnostic `'payroll'` reimbursement branch — everything else (including any unrecognised value) is `'separate'`. See module docblock's "raw wire strings" section. */
const PAYROLL_REIMBURSEMENT_METHOD = 'payroll';

const QUEUEABLE_STATUS_MAP: Readonly<Partial<Record<string, ExpenseStatus>>> = {
  submitted: 'pending',
  approved: 'approved',
  rejected: 'rejected',
  paid: 'paid',
};

/** `true` for every status the Expenses queue renders (`draft`/`voided`, and any unrecognised value, are excluded — see module docblock). */
export function isQueueableClaimStatus(status: string): boolean {
  return status in QUEUEABLE_STATUS_MAP;
}

/** Resolves `submitted_on` per the module docblock's "`submitted_on` source" section — never throws, never blank. */
export function resolveSubmittedOn(record: {
  submitted_at: string | null;
  claim_date: string;
}): string {
  return record.submitted_at !== null
    ? record.submitted_at.slice(0, 10)
    : record.claim_date;
}

/**
 * Resolves `submitter_user_id` against the employee directory. Returns the
 * matched employee's OWN `id` (never a fabricated one) plus `full_name`, or
 * `null` for both when no employee row's `employee_user_id` matches —
 * `use_expenses.ts`'s caller renders an i18n'd fallback label in that case
 * (never a hardcoded name, never `employees.at(0)` — same "no wrong-record
 * fallback" stance as `findEmployeeById`, `use_employees.ts`).
 */
export function resolveSubmitterEmployee(
  submitterUserId: string,
  employees: readonly ExpenseSubmitterLookup[],
): { id: string; full_name: string } | null {
  const match = employees.find(
    (employee) => employee.employee_user_id === submitterUserId,
  );
  return match ? { id: match.id, full_name: match.full_name } : null;
}

/**
 * Resolves a `reimbursement_method='payroll'` claim's settlement period from
 * its `payroll_run_id`, or `undefined` when the id is `null` (not yet swept
 * into a pay run — see module docblock) or doesn't match any known pay run.
 */
export function resolveReimbursementPeriod(
  payrollRunId: string | null,
  payRuns: readonly ExpensePayRunLookup[],
): PayPeriod | undefined {
  if (payrollRunId === null) return undefined;
  const match = payRuns.find((run) => run.id === payrollRunId);
  return match ? derivePayPeriod(match.period_start) : undefined;
}

/**
 * Maps one real claim record to the UI's `Expense` shape. Exported
 * separately from {@link deriveExpensesFromRecords} (mirrors
 * `wagepro_store_employees.ts`'s `toUiEmployee` split) so a single row's
 * mapping is independently testable.
 */
export function toUiExpense(
  record: RealExpenseClaimRecord,
  employees: readonly ExpenseSubmitterLookup[],
  payRuns: readonly ExpensePayRunLookup[],
): Expense {
  const status = QUEUEABLE_STATUS_MAP[record.status];
  if (status === undefined) {
    // Callers must filter with `isQueueableClaimStatus` first — this branch
    // exists only so a future exhaustiveness change to `QUEUEABLE_STATUS_MAP`
    // fails loudly (a thrown error is caught nowhere useful for a `draft`/
    // `voided` row reaching here) rather than silently rendering `undefined`.
    throw new Error(
      `toUiExpense called with a non-queueable claim status "${record.status}" -- filter with isQueueableClaimStatus first`,
    );
  }

  const submitter = resolveSubmitterEmployee(
    record.submitter_user_id,
    employees,
  );

  return {
    id: record.id,
    // The matched employee's own row id when resolvable, otherwise the raw
    // submitter user id -- both are real identifiers of the submitting
    // person, never a fabricated value. Unused for display today (see
    // `types.ts`'s field doc comment history), kept for parity with the
    // `Expense` shape.
    employee_id: submitter?.id ?? record.submitter_user_id,
    employee_name: submitter?.full_name ?? null,
    claim_number: record.claim_number,
    amount: record.total_cents,
    // The real wire currency, verbatim -- see `Expense.currency`'s own doc
    // comment in `types.ts` for why this is never collapsed to `'LKR'`.
    currency: record.currency_code,
    submitted_on: resolveSubmittedOn(record),
    status,
    reimbursement:
      record.reimbursement_method === PAYROLL_REIMBURSEMENT_METHOD
        ? {
            via: 'payroll',
            period: resolveReimbursementPeriod(record.payroll_run_id, payRuns),
          }
        : { via: 'separate' },
  };
}

/**
 * Maps every QUEUEABLE real claim record to the UI's `Expense[]` shape,
 * dropping `draft`/`voided` rows entirely (see module docblock).
 */
export function deriveExpensesFromRecords(
  records: readonly RealExpenseClaimRecord[],
  employees: readonly ExpenseSubmitterLookup[],
  payRuns: readonly ExpensePayRunLookup[],
): Expense[] {
  return records
    .filter((record) => isQueueableClaimStatus(record.status))
    .map((record) => toUiExpense(record, employees, payRuns));
}
